/**
 * Role-based access control for Cloud Office.
 * When user has stored role permissions (from signin), sidebar and routes use those.
 * Otherwise fallback: Admin & HR full access; Relationship Manager DigixLeads only; others Projects, Issues, Support Contacts.
 */

// Internal page ids used for routing & permissions.
const PAGES = {
  DASHBOARD: 'Dashboard',
  USERS: 'Users',
  PROJECTS: 'Projects',
  ISSUES: 'Issues',
  DIGIX_LEADS: 'DigixLeads',
  CLIENTS: 'Clients',
  ACCOUNTS: 'Accounts',
  HR_MANAGEMENT: 'HRManagement',
  FINANCE_MANAGEMENT: 'FinanceManagement',
  SUPPORT_CONTACTS: 'Support Contacts',
};

/** Map permission Module_Name (from GET /permissions) to sidebar page */
const MODULE_TO_PAGE = {
  'Employees': PAGES.USERS,
  'User Management': PAGES.USERS,
  'Projects': PAGES.PROJECTS,
  'Sub Projects': PAGES.PROJECTS,
  'User Stories': PAGES.PROJECTS,
  'Tasks': PAGES.PROJECTS,
  'Sub Tasks': PAGES.PROJECTS,
  'Test Cases': PAGES.PROJECTS,
  'Issues': PAGES.ISSUES,
  'Leads': PAGES.DIGIX_LEADS,
  'Digix Leads': PAGES.DIGIX_LEADS,
  'Customers': PAGES.CLIENTS,
  'Accounts': PAGES.ACCOUNTS,
  'HR Management': PAGES.HR_MANAGEMENT,
  'Finance Management': PAGES.FINANCE_MANAGEMENT,
  'Accounts Payable': PAGES.FINANCE_MANAGEMENT,
  'Accounts Receivable': PAGES.FINANCE_MANAGEMENT,
  'Support Contacts': PAGES.SUPPORT_CONTACTS,
  'Support': PAGES.SUPPORT_CONTACTS,
};

/** Path prefixes that belong to each "page" for route protection (longer prefixes first) */
const PATH_TO_PAGE = [
  ['/dashboard', PAGES.DASHBOARD],
  ['/users', PAGES.USERS],
  ['/projects', PAGES.PROJECTS],
  ['/issues', PAGES.ISSUES],
  ['/support-contacts', PAGES.SUPPORT_CONTACTS],
  ['/support-contacts/staff', PAGES.SUPPORT_CONTACTS],
  ['/support-contacts/service', PAGES.SUPPORT_CONTACTS],
  ['/support-contacts/service/add', PAGES.SUPPORT_CONTACTS],
  ['/business-leads', PAGES.DIGIX_LEADS],
  ['/training-leads', PAGES.DIGIX_LEADS],
  ['/job-leads', PAGES.DIGIX_LEADS],
  ['/clients', PAGES.CLIENTS],
  ['/accounts', PAGES.ACCOUNTS],
  ['/hr-management', PAGES.HR_MANAGEMENT],
  ['/finance-management', PAGES.FINANCE_MANAGEMENT],
  ['/followups', PAGES.DIGIX_LEADS],
  ['/', PAGES.DIGIX_LEADS],
];

const ACTIONS = {
  VIEW: 'view',
  ADD: 'add',
  EDIT: 'edit',
  DELETE: 'delete',
};

function normalizeRole(roleName) {
  if (!roleName || typeof roleName !== 'string') return '';
  return roleName.trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizePermissionName(name) {
  if (!name || typeof name !== 'string') return '';
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Admin / HR roles (backend may use "Administrator", "Human Resources", etc.) */
function isAdminOrHrLikeRole(role) {
  const r = normalizeRole(role || '');
  if (!r) return false;
  if (r === 'hr') return true;
  if (r.includes('human resource')) return true;
  if (r.includes('admin')) return true;
  return false;
}

/**
 * HR Management is a new sidebar module: backend permission rows may not list it yet.
 * Show (and allow access) for Admin/HR-like roles or anyone already granted Employees/Users.
 */
function shouldGrantHrManagementImplicitly(role, fromPermPages) {
  if (!Array.isArray(fromPermPages) || fromPermPages.length === 0) return false;
  if (isAdminOrHrLikeRole(role)) return true;
  if (fromPermPages.includes(PAGES.USERS)) return true;
  return false;
}

function shouldGrantFinanceManagementImplicitly(role, fromPermPages) {
  if (!Array.isArray(fromPermPages) || fromPermPages.length === 0) return false;
  if (isAdminOrHrLikeRole(role)) return true;
  if (fromPermPages.includes(PAGES.ACCOUNTS)) return true;
  return false;
}

function insertHrManagementBeforeSupport(pages) {
  if (pages.includes(PAGES.HR_MANAGEMENT)) return pages;
  const idx = pages.indexOf(PAGES.SUPPORT_CONTACTS);
  if (idx !== -1) {
    return [...pages.slice(0, idx), PAGES.HR_MANAGEMENT, ...pages.slice(idx)];
  }
  return [...pages, PAGES.HR_MANAGEMENT];
}

function insertFinanceManagementBeforeSupport(pages) {
  if (pages.includes(PAGES.FINANCE_MANAGEMENT)) return pages;
  const idx = pages.indexOf(PAGES.SUPPORT_CONTACTS);
  if (idx !== -1) {
    return [...pages.slice(0, idx), PAGES.FINANCE_MANAGEMENT, ...pages.slice(idx)];
  }
  return [...pages, PAGES.FINANCE_MANAGEMENT];
}

function getCurrentUserRole() {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    const user = JSON.parse(userStr);
    const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
    const roleName = (
      (primaryRole && (primaryRole.role_name || primaryRole.name)) ||
      user.role_name ||
      user.role ||
      ''
    ).toString();
    const normalized = normalizeRole(roleName);
    return normalized || null;
  } catch (e) {
    console.error("Error reading user for role access:", e?.message || e);
    return null;
  }
}

/** Stored role permissions: array of { Module_Name, Permission_Id, Permission_Name, ... } from GET /role-permission?role_id=X */
function getStoredRolePermissions() {
  try {
    const raw = localStorage.getItem('user_role_permissions');
    if (!raw) return null;
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : null;
  } catch (e) {
    return null;
  }
}

function getRequiredActionForPath(path) {
  const normalized = path && path.startsWith('/') ? path : `/${path || ''}`;

  // Treat explicit edit routes as edit
  if (normalized.includes('/edit')) return ACTIONS.EDIT;

  // Treat explicit add/create routes as add
  if (normalized.includes('/add')) return ACTIONS.ADD;

  // Default to view (list + view details)
  return ACTIONS.VIEW;
}

/** Derive allowed sidebar pages from permission list (by Module_Name). */
function getAllowedSidebarPagesFromPermissions(permissions) {
  if (!Array.isArray(permissions) || permissions.length === 0) return null;
  const pages = new Set();
  for (const p of permissions) {
    const module = (p.Module_Name || p.module_name || p.Module || p.module || '').trim();
    if (module && MODULE_TO_PAGE[module]) pages.add(MODULE_TO_PAGE[module]);
  }
  return pages.size ? Array.from(pages) : null;
}

function getAllowedActionsByPageFromPermissions(permissions) {
  if (!Array.isArray(permissions) || permissions.length === 0) return null;

  const map = new Map(); // page -> Set(actions)

  for (const p of permissions) {
    const module = p.Module_Name || p.module_name || p.Module || p.module;
    const permName = p.Permission_Name || p.permission_name || p.Permission || p.permission || '';
    if (!module) continue;

    const page = MODULE_TO_PAGE[module];
    if (!page) continue;

    const normalized = normalizePermissionName(String(permName));
    if (!map.has(page)) map.set(page, new Set());
    const set = map.get(page);

    // Normalize common permission naming conventions
    if (normalized.includes('all') || normalized.includes('full')) {
      set.add(ACTIONS.VIEW);
      set.add(ACTIONS.ADD);
      set.add(ACTIONS.EDIT);
      set.add(ACTIONS.DELETE);
      continue;
    }
    if (normalized.includes('view') || normalized.includes('read') || normalized.includes('list')) set.add(ACTIONS.VIEW);
    if (normalized.includes('add') || normalized.includes('create') || normalized.includes('new')) set.add(ACTIONS.ADD);
    if (normalized.includes('edit') || normalized.includes('update') || normalized.includes('modify')) set.add(ACTIONS.EDIT);
    if (normalized.includes('delete') || normalized.includes('remove') || normalized.includes('deactivate')) set.add(ACTIONS.DELETE);
  }

  return map.size ? map : null;
}

function canPerformActionOnPage(page, action, role) {
  const stored = getStoredRolePermissions();
  const actionsByPage = getAllowedActionsByPageFromPermissions(stored);

  // If we have stored perms, enforce action-level controls strictly.
  if (actionsByPage) {
    const set = actionsByPage.get(page);
    const fromPermPages = getAllowedSidebarPagesFromPermissions(stored);
    if (
      page === PAGES.HR_MANAGEMENT &&
      (!set || set.size === 0) &&
      shouldGrantHrManagementImplicitly(role, fromPermPages)
    ) {
      return true;
    }
    if (
      page === PAGES.FINANCE_MANAGEMENT &&
      (!set || set.size === 0) &&
      shouldGrantFinanceManagementImplicitly(role, fromPermPages)
    ) {
      return true;
    }
    if (!set || set.size === 0) return false;
    if (action === ACTIONS.VIEW) return true; // any permission on the page implies view access
    return set.has(action);
  }

  // Backward-compat fallback when permissions aren't stored on the user session
  if (isAdminOrHrLikeRole(role)) return true;

  // Without stored perms, keep legacy behavior: module visibility governs navigation only.
  // (Action-level enforcement requires backend-provided permissions.)
  return true;
}

/**
 * Returns the list of sidebar page names the current role can see.
 * When stored permissions exist, sidebar follows API modules; HR Management is merged in when
 * the role is Admin/HR-like or already has Employees, so it appears before backend adds a row.
 * Fallback by role name only when no permissions are stored.
 */
function getAllowedSidebarPages(role) {
  const stored = getStoredRolePermissions();
  const fromPerms = getAllowedSidebarPagesFromPermissions(stored);
  if (fromPerms && fromPerms.length > 0) {
    let pages = [PAGES.DASHBOARD, ...fromPerms];
    if (shouldGrantHrManagementImplicitly(role, fromPerms)) {
      pages = insertHrManagementBeforeSupport(pages);
    }
    if (shouldGrantFinanceManagementImplicitly(role, fromPerms)) {
      pages = insertFinanceManagementBeforeSupport(pages);
    }
    return pages;
  }
  if (!role) return [PAGES.DASHBOARD, PAGES.SUPPORT_CONTACTS, PAGES.PROJECTS, PAGES.ISSUES];
  const r = normalizeRole(role);
  if (isAdminOrHrLikeRole(role)) {
    return [PAGES.DASHBOARD, PAGES.USERS, PAGES.PROJECTS, PAGES.ISSUES, PAGES.DIGIX_LEADS, PAGES.CLIENTS, PAGES.ACCOUNTS, PAGES.HR_MANAGEMENT, PAGES.FINANCE_MANAGEMENT, PAGES.SUPPORT_CONTACTS];
  }
  if (r === 'relationship manager') {
    return [PAGES.DASHBOARD, PAGES.DIGIX_LEADS, PAGES.CLIENTS, PAGES.ACCOUNTS];
  }
  return [PAGES.DASHBOARD, PAGES.PROJECTS, PAGES.ISSUES, PAGES.SUPPORT_CONTACTS];
}

/**
 * Resolve which "page" a path belongs to (for route protection).
 */
function getPageForPath(path) {
  const normalized = path && path.startsWith('/') ? path : `/${path || ''}`;
  if (normalized.startsWith('/my-details') || normalized.startsWith('/change-password') || normalized.startsWith('/permissions')) return null;
  for (const [prefix, page] of PATH_TO_PAGE) {
    if (normalized === prefix || (prefix !== '/' && normalized.startsWith(prefix + '/'))) {
      return page;
    }
    if (prefix === '/' && (normalized === '/' || normalized === '')) return page;
  }
  return null;
}

/**
 * Returns true if the given path is allowed for the role.
 */
function canAccessPath(path, role) {
  const page = getPageForPath(path);
  if (page === null) return true;
  const allowed = getAllowedSidebarPages(role);
  if (!allowed.includes(page)) return false;
  const requiredAction = getRequiredActionForPath(path);
  return canPerformActionOnPage(page, requiredAction, role);
}

/**
 * Default landing path after login or when accessing unauthorized route.
 */
function getDefaultLandingPath(role) {
  const allowed = getAllowedSidebarPages(role);
  if (allowed && allowed.length > 0) {
    const first = allowed[0];
    if (first === PAGES.DASHBOARD) return '/dashboard';
    if (first === PAGES.USERS) return '/users';
    if (first === PAGES.PROJECTS) return '/projects';
    if (first === PAGES.ISSUES) return '/issues';
    if (first === PAGES.DIGIX_LEADS) return '/business-leads';
    if (first === PAGES.CLIENTS) return '/clients';
    if (first === PAGES.ACCOUNTS) return '/accounts';
    if (first === PAGES.HR_MANAGEMENT) return '/hr-management';
    if (first === PAGES.FINANCE_MANAGEMENT) return '/finance-management';
    if (first === PAGES.SUPPORT_CONTACTS) return '/support-contacts/service';
  }
  if (!role) return '/projects';
  const r = normalizeRole(role);
  if (isAdminOrHrLikeRole(role)) return '/users';
  if (r === 'relationship manager') return '/business-leads';
  return '/projects';
}

export {
  getCurrentUserRole,
  getStoredRolePermissions,
  getAllowedSidebarPages,
  canAccessPath,
  getDefaultLandingPath,
  PAGES,
  ACTIONS,
  canPerformActionOnPage,
};
